import React, { forwardRef, useRef, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useFilters } from '../../hooks/useFilters/useFilters.esm.js';
import { PillValue } from '../Pill/PillValue.esm.js';

const FilterValueButton = forwardRef((props, ref) => {
    const intl = useIntl();
    const { isOpen, items, label, name, disabled, selectedItem, enableSelectAll, ...rest } = props;
    const allItems = useRef(items);
    const { filters } = useFilters();
    // When this component is controlled, we need to keep track of all possible
    // items, as the items coming in may be a filtered set.  We don't want the
    // display to lose selected items.
    const itemValues = allItems.current.map((item) => item.value);
    const appendedItems = [...allItems.current];
    const uniqItems = useMemo(() => items.reduce((accum, item) => {
        if (!itemValues.includes(item.value)) {
            accum = accum.concat(item);
        }
        return accum;
    }, appendedItems), [items, itemValues]);
    allItems.current = uniqItems;
    const selectableItemsCount = useMemo(() => {
        if (enableSelectAll) {
            return items.filter((item) => !item.disabled).length;
        }
        else {
            return null;
        }
    }, [enableSelectAll, items]);
    const filteredItemValues = useMemo(() => items.map((item) => item.value), [items]);
    const selectedFilterValues = filters[name] || [];
    // When this component is controlled, we don't want to lose selected
    // filter values that are not present in items that may have been filtered.
    const selectedItemsNotInFilteredList = useMemo(() => Array.isArray(selectedFilterValues)
        ? allItems.current.filter((item) => !filteredItemValues.includes(item.value) &&
            selectedFilterValues.includes(item.value))
        : [], [filteredItemValues, selectedFilterValues]);
    const displayValue = useMemo(() => {
        var _a;
        const noItemSelected = !selectedItem || (Array.isArray(selectedItem) && !(selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.length));
        if (noItemSelected && !(selectedItemsNotInFilteredList === null || selectedItemsNotInFilteredList === void 0 ? void 0 : selectedItemsNotInFilteredList.length)) {
            return "";
        }
        if (enableSelectAll &&
            Array.isArray(selectedItem) &&
            selectedItem.length === selectableItemsCount) {
            return intl.formatMessage({ id: "0jcnlJ", defaultMessage: [{ type: 0, value: "All" }] });
        }
        if (Array.isArray(selectedItem)) {
            return selectedItem
                .map((item) => String(item.children || item.value))
                .concat(selectedItemsNotInFilteredList.map((item) => String(item.children || item.value)))
                .join(", ");
        }
        return (_a = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.children) !== null && _a !== void 0 ? _a : selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.value;
    }, [
        intl,
        enableSelectAll,
        selectableItemsCount,
        selectedItem,
        selectedItemsNotInFilteredList,
    ]);
    return (React.createElement(PillValue, { isFullWidth: rest.isFullWidth, labelKey: label, labelValue: displayValue, disabled: disabled, isOpen: isOpen, addClassName: "tw-rounded", required: rest.required, ref: ref, ...rest }));
});
FilterValueButton.displayName = "FilterValueButton";

export { FilterValueButton };
//# sourceMappingURL=FilterValueButton.esm.js.map
